import { CommonModule, Location } from '@angular/common';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject
} from '@angular/core';
import * as esri from 'esri-loader';
import { LIB_CONFIG, LibConfig } from '../../lib.config';
import { LoggingService } from '../../services/logging.service';
import { ScreenSizeService, ViewMode } from '../../services/screen-size';
import { GeocodeService } from './geocode.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'now-wbsuche-lib-geois-map',
  templateUrl: './geois-map.component.html',
  styleUrls: ['./geois-map.component.scss'],
  providers: [GeocodeService],
  imports: [CommonModule],
  standalone: true
})
export class GeoisMapComponent implements AfterViewInit, OnChanges {
  private readonly geocodeService = inject(GeocodeService);
  private readonly loggingService = inject(LoggingService);
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly config = inject<LibConfig>(LIB_CONFIG);
  private readonly location = inject(Location);
  private readonly changeDetector = inject(ChangeDetectorRef);

  // Es gibt fachliche Anforderungen nur das blur Hintergrundbild anzuzeigen mit einem Hinweistext
  // In diesem Falle brauchen wir die Geokoordinaten gar nicht abfragen, wenn wir sie nicht darstellen.
  @Input()
  public blurWithMessage?: string | null;

  @Input({ required: true })
  public title?: string;

  @Input({ required: true })
  public street?: string;

  @Input({ required: true })
  public zipCode?: string;

  @Input({ required: true })
  public city?: string;

  @Input()
  public controlsDisabled?: boolean;

  @Output()
  public mapError = new EventEmitter<void>();

  @ViewChild('geoisMap')
  private readonly geoisMap?: ElementRef<HTMLElement>;

  private map?: any;
  private mapView?: any;
  private geoIsError = '';

  // Classes loaded while runtime from the Ba-ArcGis Server
  private PopupTemplateClass: any;
  private GraphicClass: any;
  private PictureMarkerSymbolClass: any;
  private PointClass: any;

  public isPhablet$ = this.screenSizeService.rangeTo$(ViewMode.PHABLET);
  public showMap = false;

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.title || changes.street || changes.zipCode || changes.city) {
      this.updateMap();
    }
    if (
      changes.controlsDisabled &&
      changes.controlsDisabled.currentValue != changes.controlsDisabled.previousValue
    ) {
      if (changes.controlsDisabled.currentValue === false) {
        this.enableControls();
      } else {
        this.disableControls();
      }
    }
  }

  public ngAfterViewInit(): void {
    this.loadMapInitially();
  }

  /**
   * Initializes the ArcGis Map.
   */
  private loadMapInitially() {
    esri.setDefaultOptions({ url: this.config.geois.geoisScriptUrl });
    esri.loadCss(this.config.geois.geoisStylesheetUrl);
    esri
      .loadModules([
        'esri/Map',
        'esri/PopupTemplate',
        'esri/Graphic',
        'esri/symbols/PictureMarkerSymbol',
        'esri/geometry/Point',
        'esri/views/MapView',
        'esri/layers/ImageryLayer'
      ])
      .then(
        ([Map, PopupTemplate, Graphic, PictureMarkerSymbol, Point, MapView, ImageryLayer]) => {
          this.PopupTemplateClass = PopupTemplate;
          this.GraphicClass = Graphic;
          this.PictureMarkerSymbolClass = PictureMarkerSymbol;
          this.PointClass = Point;

          const layer = new ImageryLayer({
            url: this.config.geois.geoisImageServer
          });

          this.map = new Map({
            layers: [layer]
          });

          this.mapView = new MapView({
            container: 'geoisMap',
            map: this.map,
            ui: {
              components: []
            },
            constraints: { minScale: 1500, maxScale: 6000000 }
          });

          this.mapView.when(() => {
            this.getMapFocusElement()?.setAttribute(
              'aria-label',
              'Kartendarstellung des Veranstaltungsortes'
            );
            this.updateMap();
          });
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  /**
   * Updates the map with the current location
   */
  private readonly updateMap = () => {
    if (!!this.mapView && !!this.street && !!this.zipCode && !!this.city) {
      this.geoIsError = '';
      this.showMap = false;
      this.changeDetector.detectChanges();
      this.geocodeService.getCoordinates(this.street, this.zipCode, this.city).subscribe({
        next: (coords) => {
          const point = new this.PointClass({
            x: Number(coords.x),
            y: Number(coords.y),
            spatialReference: { wkid: 25832 }
          });

          const pictureMarkerSymbol = new this.PictureMarkerSymbolClass({
            url: this.location.prepareExternalUrl('assets/images/map-marker-35x35.png'),
            width: 35,
            height: 35
          });

          const infoTemplate = new this.PopupTemplateClass({
            title: this.title,
            content: `${this.street}</br>${this.zipCode} ${this.city}`,
            actions: [],
            // To Remove the ZoomTo Button
            overwriteActions: true
          });

          const graphic = new this.GraphicClass({
            geometry: point,
            symbol: pictureMarkerSymbol,
            popupTemplate: infoTemplate
          });

          this.mapView.graphics.removeAll();
          this.mapView.graphics.add(graphic);
          this.mapView.goTo({
            target: graphic,
            scale: 7000
          });
          this.showMap = true;
          this.changeDetector.detectChanges();
          // TODO Remove when DPL-ShowMoreComponent is a11y
          this.enableControls();
        },
        error: (error) => {
          this.handleError(error);
        }
      });
    }
  };

  private handleError(error: any): void {
    this.loggingService.debug('ERROR_MESSAGE_NOT_AVAILABLE');
    this.loggingService.error('Fehler: ' + error);
    this.geoIsError = 'Kartenansicht aktuell nicht verfügbar. Bitte später erneut versuchen...';
    this.showMap = false;
    this.mapError.emit();
    this.changeDetector.detectChanges();
  }

  private disableControls() {
    if (this.mapView) {
      this.mapView.ui.components = [];
    }
    const mapElement = this.getMapFocusElement();
    mapElement?.setAttribute('tabindex', '-1');
  }

  private enableControls() {
    if (this.mapView) {
      this.mapView.ui.components = ['attribution', 'zoom'];
    }
    const mapElement = this.getMapFocusElement();
    mapElement?.setAttribute('tabindex', '0');
  }

  private readonly getMapFocusElement = () => {
    return this.geoisMap?.nativeElement.querySelector('[role="application"]');
  };

  get isMapVisible(): boolean {
    return this.showMap && !this.blurWithMessage;
  }

  get errorMessage(): string {
    return this.blurWithMessage ? this.blurWithMessage : this.geoIsError;
  }
}
